import React, { useState, useRef, useEffect } from 'react';
import { apiBaseUrl } from '../../config/runtime';
import { useTranslation } from 'react-i18next';
import './ChatRoomTitleEditor.css';

interface ChatRoomTitleEditorProps {
  roomId: number;
  currentTitle: string;
  onTitleUpdate: (roomId: number, newTitle: string) => void;
  onCancel: () => void;
}

const ChatRoomTitleEditor: React.FC<ChatRoomTitleEditorProps> = ({
  roomId,
  currentTitle,
  onTitleUpdate,
  onCancel
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(currentTitle);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 컴포넌트가 마운트되면 입력 필드에 포커스
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    const newTitle = title.trim();
    
    // 제목이 변경되지 않았으면 취소
    if (newTitle === currentTitle) {
      onCancel();
      return;
    }

    setIsLoading(true);
    
    try {
      // API 호출하여 제목 업데이트
      const response = await fetch(apiBaseUrl(`/chat/room/${roomId}/title`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ title: newTitle })
      });

      const data = await response.json();
      
      if (data.success) {
        onTitleUpdate(roomId, newTitle || currentTitle);
      } else {
        console.error('제목 업데이트 실패:', data.message);
        alert(t('roomList.updateFailed'));
        onCancel();
      }
    } catch (error) {
      console.error('제목 업데이트 오류:', error);
      alert(t('roomList.updateError'));
      onCancel();
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleBlur = () => {
    // 포커스를 잃으면 자동으로 저장
    if (!isLoading) {
      handleSubmit(new Event('submit') as any);
    }
  };

  return (
    <div className="chat-room-title-editor">
      <form onSubmit={handleSubmit} className="title-editor-form">
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="title-editor-input"
          placeholder={t('roomList.titlePlaceholder')}
          maxLength={50}
          disabled={isLoading}
        />
        {isLoading && (
          <div className="title-editor-loading">
            <div className="loading-spinner"></div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ChatRoomTitleEditor;
